import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, OnChanges, SimpleChanges, Output, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'vs-tree-organization',
  templateUrl: './vs-tree-organization.component.html',
  styleUrls: ['./vs-tree-organization.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => VsTreeOrganizationComponent),
      multi: true
    }
  ]
})
export class VsTreeOrganizationComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() multiple: boolean = false;
  @Output() selectionChanged = new EventEmitter<any[]>();
  @Input() parentNodes: any[] = [];
  @Input() data: any[] = [];
  @Input() expandLevel: number = 2;
  @Input() parentId: any = null;
  @Input() dropdown: boolean = false;
  @Input() autoSelectFirst: boolean = false;
  @Input() label: string = 'Nhóm tổ chức';
  @Input() searchPlaceholder: string = 'Tìm kiếm...';
  @Input() showFullSelectedLabels: boolean = false;
  @Input() cascadeSelection: boolean = true;
  @Input() panelPosition: string = 'bottom';
  @Input() panelMaxHeight: number = 390;
  @Input() expandOnOpen: boolean = false;

  nodes: any[] = [];
  private expandedOnce: boolean = false;
  private originalFlatData: any[] = [];
  private inputNodesData: any[] = [];
  private selectedIds: any = null;
  private autoSelectedOnce: boolean = false;

  searchTerm: string = '';
  dropdownOpen: boolean = false;
  selectedLabel: string = '';
  selectedLabelItems: string[] = [];

  onChange: any = () => { };
  onTouched: any = () => { };

  constructor(private elementRef: ElementRef) { }

  ngOnInit() {
    this.prepareNodes();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data'] || changes['parentId']) {
      this.expandedOnce = false;
      this.autoSelectedOnce = false;
      this.prepareNodes();
    }
  }

  private prepareNodes(): void {
    var sourceNodes = this.getSourceNodes();

    if (sourceNodes && sourceNodes.length > 0) {
      if (!this.isTreeData(sourceNodes)) {
        this.originalFlatData = sourceNodes;
      }

      var displaySource = this.searchTerm && this.originalFlatData.length > 0
        ? this.filterFlatData(this.originalFlatData, this.searchTerm)
        : sourceNodes;

      this.nodes = this.isTreeData(displaySource) ? displaySource : this.buildTree(displaySource, this.parentId);
      this.normalizeNodes(this.nodes);
      if (this.searchTerm) {
        this.expandAll(this.nodes);
      }
    }
    if (!sourceNodes || sourceNodes.length === 0) {
      this.originalFlatData = [];
      this.nodes = [];
    }

    this.tryAutoExpand();
    this.updateSelectedLabel();
    this.tryAutoSelectFirst();
  }

  private getSourceNodes(): any[] {
    if (this.data && this.data.length > 0) {
      return this.data;
    }

    if (this.inputNodesData && this.inputNodesData.length > 0) {
      return this.inputNodesData;
    }

    return this.nodes || [];
  }

  private tryAutoExpand(): void {
    if (this.expandedOnce || !this.nodes || this.nodes.length === 0 || this.expandLevel <= 0) return;
    this.autoExpand(this.nodes, 0);
    this.expandedOnce = true;
  }

  private autoExpand(nodes: any[], currentLevel: number): void {
    if (!nodes || currentLevel >= this.expandLevel) return;
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].children && nodes[i].children.length > 0) {
        nodes[i].expanded = true;
        this.autoExpand(nodes[i].children, currentLevel + 1);
      }
    }
  }

  writeValue(obj: any[]): void {
    if (obj) {
      this.inputNodesData = obj;
      this.nodes = obj;
      this.prepareNodes();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void { }

  addParentNode(node: any, parentNodes: any[]): any[] {
    return (parentNodes || []).concat([node]);
  }

  toggleExpand(node: any): void {
    node.expanded = !node.expanded;
  }

  toggleCheckbox(node: any, parentNodes: any[] = this.parentNodes): void {
    node.checked = !node.checked;

    if (!this.multiple) {
      this.clearSelections(this.nodes);
      this.clearParentSelection(node, parentNodes);
      node.checked = true;
      this.emitSelection([node]);
      this.selectedLabel = node.name || this.label;
      this.selectedLabelItems = node.name ? [node.name] : [];
      if (this.dropdown) {
        this.dropdownOpen = false;
      }
    } else {
      // Cascade: check/uncheck tất cả children
      if (this.cascadeSelection && node.children && node.children.length > 0) {
        this.setChildrenChecked(node.children, node.checked);
      }
      const selectedNodes = this.getSelectedNodes(this.nodes);
      this.emitSelection(selectedNodes);
      this.updateSelectedLabel();
    }
  }

  emitSelection(items: any[]): void {
    this.updateSelectedIds(items);
    this.selectionChanged.emit(items);
    this.onChange(items);
  }

  private tryAutoSelectFirst(): void {
    if (!this.autoSelectFirst || this.multiple || this.autoSelectedOnce || this.searchTerm) return;
    if (!this.nodes || this.nodes.length === 0 || this.hasSelectedIds()) return;

    var firstNode = this.nodes[0];
    if (!firstNode) return;

    this.clearSelections(this.nodes);
    firstNode.checked = true;
    this.autoSelectedOnce = true;
    this.selectedLabel = firstNode.name || this.label;
    this.emitSelection([firstNode]);
  }

  getSelectedNodes(nodes: any[]): any[] {
    var selectedNodes: any[] = [];
    this.walkNodes(nodes, function (node) {
      if (node.checked) {
        selectedNodes.push(node);
      }
    });
    return selectedNodes;
  }

  clearParentSelection(node: any, parentNodes: any[]): void {
    if (!parentNodes) return;

    this.walkNodes(parentNodes, function (currentNode) {
      currentNode.checked = false;
    });
  }

  setChildrenChecked(children: any[], checked: boolean): void {
    this.walkNodes(children, function (node) {
      node.checked = checked;
    });
  }

  clearSelections(nodes: any[]): void {
    this.walkNodes(nodes, function (node) {
      node.checked = false;
    });
  }

  isIndeterminate(node: any): boolean {
    if (!node.children || node.children.length === 0) return false;
    var hasChecked = false;
    var hasUnchecked = false;
    var stack = node.children.slice();
    while (stack.length > 0) {
      var current = stack.pop();
      if (current.checked) {
        hasChecked = true;
      } else {
        hasUnchecked = true;
      }
      if (hasChecked && hasUnchecked) return true;
      if (current.children) {
        for (var i = 0; i < current.children.length; i++) {
          stack.push(current.children[i]);
        }
      }
    }
    return hasChecked && hasUnchecked;
  }

  hasChildren(node: any): boolean {
    return node.children && node.children.length > 0;
  }

  trackByFn(index: number, item: any): any {
    if (!item) return index;
    return item.id !== undefined && item.id !== null ? item.id : index;
  }

  private walkNodes(nodes: any[], handler: (node: any) => void): void {
    if (!nodes || nodes.length === 0) return;

    var stack = nodes.slice();
    while (stack.length > 0) {
      var current = stack.pop();
      if (!current) continue;

      handler(current);

      if (current.children && current.children.length > 0) {
        for (var i = 0; i < current.children.length; i++) {
          stack.push(current.children[i]);
        }
      }
    }
  }

  toggleDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.dropdownOpen = !this.dropdownOpen;
    if (this.dropdownOpen && this.expandOnOpen) {
      this.expandedOnce = false;
      this.prepareNodes();
    }
  }

  onSearch(event: any): void {
    this.searchTerm = (event.target.value || '').toLowerCase();
    this.prepareNodes();
  }

  clearAll(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }

    this.searchTerm = '';
    this.selectedIds = null;
    this.autoSelectedOnce = true;
    this.clearSelections(this.nodes);
    this.expandedOnce = false;
    this.prepareNodes();
    this.selectedLabel = this.label;
    this.selectedLabelItems = [];
    this.emitSelection([]);
  }

  hasSelection(): boolean {
    return this.hasSelectedIds();
  }

  showSelectedTags(): boolean {
    return this.showFullSelectedLabels && this.multiple && this.selectedLabelItems.length > 0;
  }

  onPanelClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.dropdown && !this.elementRef.nativeElement.contains(event.target)) {
      this.dropdownOpen = false;
    }
  }

  buildTree(data: any[], parentId: any = null): any[] {
    if (!data || data.length === 0) {
      return [];
    }

    var ids: any = {};
    var childrenByParent: any = {};
    for (var i = 0; i < data.length; i++) {
      var item = data[i];
      ids[this.getLookupKey(item.id)] = true;

      var parentKey = this.getLookupKey(item.idParent);
      if (!childrenByParent[parentKey]) {
        childrenByParent[parentKey] = [];
      }
      childrenByParent[parentKey].push(item);
    }

    var rootNodes = parentId != null
      ? this.findRootNodesByPath(data, parentId)
      : data.filter(item => item.idParent == null || !ids[this.getLookupKey(item.idParent)]);
    var sourceNodes = rootNodes.length > 0
      ? rootNodes
      : data.filter(item => item.idParent == null || !ids[this.getLookupKey(item.idParent)]);

    return this.buildTreeBySource(sourceNodes, childrenByParent);
  }

  private findRootNodesByPath(data: any[], parentId: any): any[] {
    var parentIdValue = Number(parentId);
    var nodesInPath = data.filter(item =>
      this.getPathIds(item).indexOf(parentIdValue) >= 0 ||
      Number(item.id) === parentIdValue ||
      Number(item.idParent) === parentIdValue);

    if (nodesInPath.length === 0) {
      return data.filter(item => item.idParent === parentId);
    }

    var branchIds = nodesInPath.map(item => Number(item.id));
    return nodesInPath.filter(item => {
      var itemId = Number(item.id);
      var pathIds = this.getPathIds(item).filter(id => branchIds.indexOf(id) >= 0 && id !== itemId);
      return pathIds.length === 0;
    });
  }

  private getPathIds(item: any): number[] {
    var path = item && (item.path || item.Path) ? (item.path || item.Path).toString() : '';
    return path
      .split(';')
      .filter(id => id !== '')
      .map(id => Number(id))
      .filter(id => !isNaN(id));
  }

  private buildTreeBySource(sourceNodes: any[], childrenByParent: any): any[] {
    var result = [];

    sourceNodes.forEach(item => {
      var childrenSource = childrenByParent[this.getLookupKey(item.id)] || [];
      var children = this.buildTreeBySource(childrenSource, childrenByParent);
      var newNode = {
        idParent: item.idParent,
        id: item.id,
        name: item.name,
        path: item.path || item.Path,
        idClient: item.idClient,
        checked: item.checked === true || (this.selectedIds && this.selectedIds[this.getLookupKey(item.id)] === true),
        expanded: item.expanded === true,
        children: children.length ? children : undefined
      };
      result.push(newNode);
    });

    return result;
  }

  private getLookupKey(value: any): string {
    return value === undefined || value === null ? '__root__' : value.toString();
  }

  private filterFlatData(data: any[], searchTerm: string): any[] {
    if (!data || !searchTerm) return data || [];

    var normalizedTerm = this.normalizeText(searchTerm);
    var visibleIds: any = {};

    data.forEach(item => {
      var itemName = this.normalizeText(item.name || '');
      if (itemName.indexOf(normalizedTerm) >= 0) {
        var itemId = this.getLookupKey(item.id);
        visibleIds[itemId] = true;
        this.getPathIds(item).forEach(id => visibleIds[this.getLookupKey(id)] = true);
      }
    });

    return data.filter(item => {
      var itemId = this.getLookupKey(item.id);
      return visibleIds[itemId] === true;
    });
  }

  private normalizeText(value: string): string {
    return (value || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/đ/g, 'd');
  }

  private updateSelectedLabel(): void {
    var selectedNodes = this.getSelectedNodes(this.nodes);
    this.updateSelectedIds(selectedNodes);

    if (!selectedNodes || selectedNodes.length === 0) {
      this.selectedLabel = this.label;
      this.selectedLabelItems = [];
      return;
    }

    this.selectedLabelItems = selectedNodes
      .map(node => node && node.name)
      .filter(name => !!name);

    if (this.multiple && selectedNodes.length > 1) {
      this.selectedLabel = this.showFullSelectedLabels
        ? selectedNodes.map(node => node.name).join(', ')
        : selectedNodes.length + ' mục đã chọn';
      return;
    }

    this.selectedLabel = selectedNodes[0].name;
  }

  private updateSelectedIds(items: any[]): void {
    this.selectedIds = {};
    if (!items) return;

    for (var i = 0; i < items.length; i++) {
      if (items[i] && items[i].id !== undefined && items[i].id !== null) {
        this.selectedIds[this.getLookupKey(items[i].id)] = true;
      }
    }
  }

  private expandAll(nodes: any[]): void {
    if (!nodes) return;
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].children && nodes[i].children.length > 0) {
        nodes[i].expanded = true;
        this.expandAll(nodes[i].children);
      }
    }
  }

  private hasSelectedIds(): boolean {
    if (!this.selectedIds) return false;
    for (var key in this.selectedIds) {
      if (this.selectedIds.hasOwnProperty(key)) return true;
    }
    return false;
  }

  private isTreeData(nodes: any[]): boolean {
    if (!nodes || nodes.length === 0) return false;
    return nodes.some(node => node && node.children && node.children.length > 0);
  }

  private normalizeNodes(nodes: any[]): void {
    if (!nodes) return;

    for (var i = 0; i < nodes.length; i++) {
      if (!nodes[i]) continue;

      if (!nodes[i].name && nodes[i].label) {
        nodes[i].name = nodes[i].label;
      }

      if (nodes[i].children && nodes[i].children.length > 0) {
        this.normalizeNodes(nodes[i].children);
      }
    }
  }
}
